<template>
	<card no-spacing flat :loading="props.trackSpot._loading" max-height="500">
		<card-title v-if="title">{{ title }}</card-title>

		<card-text class="overflow-y-auto" fill>
			<p v-if="props.trackSpot.description" class="whitespace-pre-wrap">{{ props.trackSpot.description }}</p>

			<info-list small>
				<info-list-item v-if="props.trackSpot.track" :icon="icons.track"
								:text="props.trackSpot.track.name"/>

				<info-list-item v-for="(listItem, listItemKey) in listItems" :key="listItemKey" :icon="listItem.icon"
								:text="listItem.text"/>

				<info-list-item v-if="commonDistanceFromStart" :icon="icons.distance"
								:text="n(commonDistanceFromStart / 1000, {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' km'"/>

				<info-list-item v-if="props.trackSpot.radius" :icon="icons.radius"
								:text="`${props.trackSpot.radius} Meter`"/>

				<info-list-item v-if="props.trackSpot.is_visible === false" :icon="icons.invisible"
								:text="t('label.invisible')"/>
			</info-list>
			<slot/>
		</card-text>

		<wrapper v-if="props.editable" class="px-16 py-16 lg:px-24 border-box border-t" stretch>
			<btn small @click="emit('move')" :title="t('action.move')">
				<Icon :icon="icons.move"/>
			</btn>
			<btn small color="success" @click="emit('edit')" :title="t('action.edit')">
				<Icon :icon="icons.edit"/>
			</btn>
			<btn small color="error" @click="emit('destroy')" :title="t('action.destroy')">
				<Icon :icon="icons.destroy"/>
			</btn>
		</wrapper>

	</card>
</template>

<script setup lang="ts">
import {
	mdiClockStart,
	mdiEyeOffOutline,
	mdiFlagCheckered,
	mdiMapMarkerDistance,
	mdiMotionPauseOutline,
	mdiMotionPlayOutline,
	mdiRadiusOutline,
	mdiTimelineClockOutline,
	mdiTimerOutline,
	mdiUpdate,
	mdiVectorPolyline
} from "@mdi/js";
import {destroyIcon, editIcon, moveIcon} from "../../config";
import {useI18n} from '#imports';
import {groupBy, TrackSpotSubtype, TrackSpotType} from "@spoferan/spoferan-ts-core";
import {computed} from "vue";

const props = defineProps<{
	trackSpot: object,
	editable: boolean,
}>();
const emit = defineEmits(['move', 'edit', 'destroy']);

const {t, n} = useI18n();

const icons = {
	start: mdiClockStart,
	finish: mdiFlagCheckered,
	result_tracking_spot: mdiTimelineClockOutline,
	round_result_tracking_spot: mdiUpdate,
	invisible: mdiEyeOffOutline,
	radius: mdiRadiusOutline,
	distance: mdiMapMarkerDistance,
	track: mdiVectorPolyline,
	edit: editIcon,
	move: moveIcon,
	destroy: destroyIcon
}

const uniqueParticipationClasses = computed(() => {
	return Object.values(groupBy(props.trackSpot.participationClasses, 'id'));
});

const groupedParticipationClasses = computed(() => {
	return Object.values(groupBy(props.trackSpot.participationClasses, participationClass => `${participationClass.pivot.position}_${participationClass.pivot.distance_from_start}`));
});

const listItems = computed(() => {
	const listItems = [];
	groupedParticipationClasses.value.forEach(participationClassGroup => {
		if (participationClassGroup.length === uniqueParticipationClasses.value.length) {
			listItems.push(buildTypeInfoListItem(participationClassGroup[0], false, groupedParticipationClasses.value.length > 1))
		} else {
			participationClassGroup.forEach(participationClass => {
				listItems.push(buildTypeInfoListItem(participationClass, true, true))
			})
		}
	});
	const subtypeItem = buildSubtypeInfoListItem();
	if (subtypeItem) {
		listItems.push(subtypeItem);
	}

	return listItems;
});

const commonDistanceFromStart = computed(() => {
	const participationClasses = props.trackSpot.participationClasses;
	if (participationClasses && participationClasses.length) {
		const distances = participationClasses.map(participationClass => participationClass.pivot.distance_from_start);

		// Filter duplicates
		const uniqueDistances = distances.filter((item, index) => distances.indexOf(item) === index);

		return uniqueDistances.length === 1 ? uniqueDistances[0] : null;
	}
	return null;
});

const title = computed(() => {
	if (props.trackSpot.title) {
		return props.trackSpot.title;
	}

	switch (props.trackSpot.type) {
		case TrackSpotType.Navigation:
			return t('label.navigation_hint');
	}

	return null
});

function buildTypeInfoListItem(participationClass, withName = false, withDistance = false) {
	let text = withName ? `${participationClass.name}: ` : '';
	let icon = icons.result_tracking_spot;

	switch (props.trackSpot.type) {
		case TrackSpotType.Result:
			if (participationClass.pivot.position === 1 && participationClass.pivot.is_finish) {
				text += `${t('label.start_station')} / ${t('label.finish_station')}`;
				icon = icons.round_result_tracking_spot;
				withDistance = false;
			} else if (participationClass.pivot.position === 1) {
				text += t('label.start_station');
				icon = icons.start;
				withDistance = false;
			} else if (participationClass.pivot.is_finish) {
				text += t('label.finish_station');
				icon = icons.finish;
			} else {
				text += `${t('label.result_tracking_spot')} ${participationClass.pivot.position - 1}`;
				icon = icons.result_tracking_spot;
			}
			break;
		case TrackSpotType.Control:
			text += `${t(`enum.track_spot_type.${props.trackSpot.type}.title`)}`
			break;
		case TrackSpotType.Navigation:
			text += `${t(`enum.track_spot_type.${props.trackSpot.type}.title`)} ${participationClass.pivot.position}`
			break;
	}

	if (withDistance) {
		text += ` (${n(participationClass.pivot.distance_from_start / 1000, {minimumFractionDigits: 2, maximumFractionDigits: 2})} km)`
	}

	return {
		text: text,
		icon: icon
	};
}

function buildSubtypeInfoListItem() {
	if (!props.trackSpot.subtype) {
		return null;
	}

	const text = t(`enum.track_spot_subtype.${props.trackSpot.subtype}.title`);
	switch (props.trackSpot.subtype) {
		case TrackSpotSubtype.ResultTimeMeasurement:
			return {
				icon: mdiTimerOutline,
				text: text
			}
		case TrackSpotSubtype.ResultTimePause:
			return {
				icon: mdiMotionPauseOutline,
				text: text
			}
		case TrackSpotSubtype.ResultTimeContinue:
			return {
				icon: mdiMotionPlayOutline,
				text: text
			}
	}

	return null;
}
</script>